import { CSSProperties } from 'react';
import { ColumnType } from '../../types';
import { Customer } from './customer';

export * from './customer';
export * from './user';
export * from './price-group';

export interface Product {
  _id: string;
  productCode: string;
  name: string;
  active: boolean;
  cost?: number;
  customerCost?: number;
  description: string;
  discontinued: boolean;
  dropship: boolean;
  manufacturerProductNumber: string;
  price?: number | null;
  sell?: number | null;
  imageUrl: string;
  packSize: string;
  reasoning?: string;
  percentage?: number;
  unit: string;
  special: boolean;
  procurementStopped: boolean;
  salesStopped: boolean;
  created_on: string;
  updated_on: string;
  last_modified: string;
  images: ProductImage[] | null;
  inventory: ProductInventory[] | null;
  sku: string | null;
  manufacturerSku: string | null;
  vendor: Vendor | null;
  categories: ProductCategory[] | null;
  custom?: DynamicAttribute;
  privateLabel?: boolean;
  promotions: string[] | null;
  productAssociations?: string[];
  isStockProduct?: boolean;
  isEditable?: boolean;
  stockInfo?: StockInfo;
  quantity?: number;
  vendorHierarchy?: number;
  isPrivateLabel?: boolean;
  isPreferredVendor?: boolean;
  vendorRanking?: number;
  unitOfMeasure?: string;
  minimumQuoteQuantity?: number;
  overrideMinimumQuoteQuantity?: boolean;
  /**
   * The last pricing call produced no usable price for this SKU (DEV-495).
   * Distinct from `price == null` on a product nobody has priced yet, and from a
   * legitimate `price === 0`.
   */
  pricingFailed?: boolean;
}

export interface StockInfo {
  /**
   * Stocked in at least one warehouse. Read the badge from HERE — the
   * product's own top-level `isStockProduct` is passed through as stored and
   * Singer never fills it.
   */
  isStockProduct?: boolean;
  allWarehouses: boolean;
  warehouses: Warehouse[];
}

export interface Warehouse {
  warehouseId: string;
  stock: boolean;
  warehouseName: string;
}

export interface TempProductType {
  _id: string;
  productCode: string;
  name: string;
  active: boolean;
  cost?: number;
  description: string;
  discontinued: boolean;
  dropship: boolean;
  manufacturerProductNumber: string;
  price: number | null;
  imageUrl: string;
  packSize: string;
  unit: string;
  special: boolean;
  procurementStopped: boolean;
  salesStopped: boolean;
  created_on: string;
  updated_on: string;
  last_modified: string;
  images: string;
  inventory: ProductInventory[] | null;
  sku: string | null;
  manufacturerSku: string | null;
  vendor: Vendor | null;
  categories: ProductCategory[] | null;
  custom?: DynamicAttribute;
  privateLabel?: boolean;
  promotions: string[] | null;
  productAssociations?: string[];
  minimumQuoteQuantity?: number;
  overrideMinimumQuoteQuantity?: boolean;
}

export interface ProductCategoryBreadcrumb {
  _id: string;
  name: string;
}

export interface ProductCategory {
  _id: string;
  parentId: string;
  name: string;
  lastModifiedDate: string;
  breadcrumbs: ProductCategoryBreadcrumb[];
}

export interface ProductImage {
  _id: string;
  path: string;
  size: string;
  name: string;
}

export interface ProductInventory {
  _id: string;
  productId: string;
  siteId: string;
  warehouseId: string;
  availableOnHand: number;
  availableOrderedQuantity: number;
  onHandQuantity: number;
  onOrderQuantity: number;
  orderedQuantity: number;
  onTransferQuantity: number;
  warehouse: ProductWarehouse | null;
}

export interface ProductWarehouse {
  _id: string;
  code: string;
  name: string;
  address1: string;
  address2: string;
  address3: string;
  city: string;
  state: string;
  zip: string;
}
export interface Vendor {
  _id: string;
  vendorId: string;
  name: string;
}

export interface DynamicAttribute {
  attribute: RecordType;
  ship_lead_time__business_days?: string;
  leadTime?: string;
  ingredients?: RecordType;
}

export interface RecordType {
  value: string | boolean;
  label: string;
  type: CustomPropertyType;
  specification: boolean;
}

export enum ProductDetailsTabsIds {
  PRODUCT_INFO = 'productInfo',
  WAREHOUSE = 'warehouse',
  ASSOCIATED_RESOURCE = 'associatedResources',
  ASSOCIATED_PRODUCTS = 'associatedProducts',
  SIMILAR_PRODUCTS = 'similarProducts',
  SALES_ENABLEMENT = 'salesEnablement',
  ORDER_HISTORY = 'orderHistory',
  PRODUCT_ENHANCER = 'productEnhancer'
}

export enum ProductColumnsIds {
  ProductName = 'productName',
  Manufacturer = 'manufacturer',
  Stock = 'stock',
  LeadTime = 'leadTime',
  Cost = 'cost',
  GetCost = 'getCost',
  CustomerCost = 'customerCost',
  Price = 'price',
  ListPrice = 'listPrice',
  GetPrice = 'getPrice',
  GP = 'gp',
  Discount = 'discount',
  Family = 'family',
  Line = 'line',
  Size = 'size'
}

export enum ProductDetailsColumnsIds {
  Location = 'location',
  Stocked = 'stocked',
  OrderedQuantity = 'orderedQuantity',
  OnOrderQuantity = 'onOrderQuantity',
  OnHandQuantity = 'onHandQuantity',
  OnTransferQuantity = 'onTransferQuantity',
  Stock = 'stock'
}

export enum CatalogCustomerViewOptions {
  Required = 'required',
  Optional = 'optional'
}

export interface CatalogCustomerView {
  hide: CatalogCustomerViewOptions;
  optionLabel?: string;
  columnLabel?: string;
}

export interface ProductColumn extends Pick<
  ColumnType,
  'id' | 'label' | 'width' | 'minWidth' | 'maxWidth'
> {
  customerView?: CatalogCustomerView;
}

export enum ProductPriceType {
  None = 'none', // Default
  List = 'list', // Middleby (manufacturer)
  Get = 'get', // Distributor
  Full = 'full', // Singer, Telabio
  GetFull = 'getFull' // PJP
}

export interface Pricing {
  id:
    | ProductColumnsIds.Price
    | ProductColumnsIds.Cost
    | ProductColumnsIds.CustomerCost
    | ProductColumnsIds.GP
    | ProductColumnsIds.Discount;
  label: string;
  bold?: boolean;
}

export interface GridPricing extends Pricing {
  align?: CSSProperties['textAlign'];
  fullWidth?: boolean;
}

export interface ProductStock {
  show: boolean;
  refresh: boolean;
}

export interface ProductDetails {
  tabs: ProductDetailsTab[];
  pricing: Pricing[];
  stock: ProductStock;
}

export interface ProductDetailsTab {
  id: ProductDetailsTabsIds;
  columns?: ProductColumn[];
}

export interface ProductGridItem {
  manufacturerLeadTime: boolean;
  manufacturerSku: boolean;
  pricing: GridPricing[];
  stock: ProductStock;
}
export interface ProductFilterCustomer {
  show: boolean;
  value: Customer;
  onChange: (customer: Customer) => void;
}

export interface ProductFilterPriceGroup {
  show: boolean;
  value: any;
  onChange: (priceGroup: any) => void;
}

export interface CatalogConfig {
  filters: {
    customer: ProductFilterCustomer;
    priceGroup: ProductFilterPriceGroup;
  };
  product: {
    columns: ProductColumn[];
    grid: ProductGridItem;
  };
  productDetails: ProductDetails;
  price: ProductPriceType;
}

export enum CustomPropertyType {
  String = 'string',
  Number = 'number',
  Boolean = 'boolean',
  Link = 'link',
  HTML = 'html',
  ArrayString = 'arrayString',
  LabeledImageArray = 'LabeledImageArray'
}

export enum CompanyProductUserType {
  Distributor = '0',
  Manufacturer = '1'
}
